import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Badge,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  getAdminCommissionStructures,
  updateUserCommissionAmount,
  setUserCommissionStructure,
  getDashboardUsersPaginated,
} from "../../../slices/admin/thunk";
import { formatCentsToDollars } from "../../../utils/utils";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const UserCommissionManagementModal = ({ isOpen, setIsOpen, currentUser }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Admin access validation
  const isCurrentUserAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [commissionStructures, setCommissionStructures] = useState([]);

  // Form states
  const [customAmount, setCustomAmount] = useState("");
  const [selectedStructureId, setSelectedStructureId] = useState("");
  const [amountError, setAmountError] = useState("");
  const [structureError, setStructureError] = useState("");

  const resetModal = () => {
    setSearchQuery("");
    setSelectedUser(null);
    setSearchResults([]);
    setCustomAmount("");
    setSelectedStructureId("");
    setAmountError("");
    setStructureError("");
  };

  const handleClose = () => {
    resetModal();
    setIsOpen(false);
  };

  const fetchCommissionStructures = async () => {
    try {
      const response = await dispatch(getAdminCommissionStructures({}));
      if (response.payload?.commissionStructures) {
        setCommissionStructures(response.payload.commissionStructures);
      }
    } catch (error) {
      console.error("Error fetching commission structures:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCommissionStructures();
    }
  }, [isOpen]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await dispatch(getDashboardUsersPaginated({
        page: 1,
        query: searchQuery,
        sortBy: "firstName",
        sortOrder: "asc",
      }));

      if (response.payload?.users) {
        setSearchResults(response.payload.users);
      }
    } catch (error) {
      console.error("Error searching users:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to search users",
        icon: "error",
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setCustomAmount(user.customCommissionTotalAmount || "1000");
    setSelectedStructureId(user.customCommissionStructureId || "");
    setSearchResults([]);
    setSearchQuery("");
  };

  const validateAmountForm = () => {
    let isValid = true;
    if (!customAmount || parseFloat(customAmount) < 0) {
      setAmountError("Amount must be a positive number");
      isValid = false;
    } else {
      setAmountError("");
    }
    return isValid;
  };

  const validateStructureForm = () => {
    let isValid = true;
    if (!selectedStructureId) {
      setStructureError("Please select a commission structure");
      isValid = false;
    } else {
      setStructureError("");
    }
    return isValid;
  };

  const handleUpdateAmount = async () => {
    if (!validateAmountForm()) return;

    try {
      setLoading(true);
      await dispatch(updateUserCommissionAmount({
        userId: selectedUser.id,
        customCommissionTotalAmount: parseFloat(customAmount),
      }));

      Swal.fire({
        title: "Success!",
        text: "Commission amount updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Update selected user data
      setSelectedUser(prev => ({
        ...prev,
        customCommissionTotalAmount: parseFloat(customAmount),
      }));
    } catch (error) {
      console.error("Error updating commission amount:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update commission amount",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStructure = async () => {
    if (!validateStructureForm()) return;

    try {
      setLoading(true);
      await dispatch(setUserCommissionStructure({
        userId: selectedUser.id,
        customCommissionStructureId: selectedStructureId === "default" ? null : selectedStructureId,
      }));

      Swal.fire({
        title: "Success!",
        text: "Commission structure updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Update selected user data
      setSelectedUser(prev => ({
        ...prev,
        customCommissionStructureId: selectedStructureId === "default" ? null : selectedStructureId,
      }));
    } catch (error) {
      console.error("Error updating commission structure:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update commission structure",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return <Badge color="primary" pill>National</Badge>;
      case DASHBOARD_USER_ROLES.GA:
        return <Badge color="info" pill>GA</Badge>;
      case DASHBOARD_USER_ROLES.BROKER:
        return <Badge color="success" pill>Broker</Badge>;
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return <Badge color="warning" pill>Employer</Badge>;
      case DASHBOARD_USER_ROLES.ADMIN:
        return <Badge color="danger" pill>Admin</Badge>;
      default:
        return <Badge color="secondary" pill>{role}</Badge>;
    }
  };

  const getCurrentStructureName = () => {
    if (!selectedUser?.customCommissionStructureId) {
      return "Default Structure";
    }
    const structure = commissionStructures.find(s => s.id === selectedUser.customCommissionStructureId);
    return structure ? structure.name : "Unknown Structure";
  };

  const getCurrentStructureDetails = () => {
    if (!selectedUser?.customCommissionStructureId) {
      return { national: 300, ga: 300, broker: 400 }; // Default structure
    }
    const structure = commissionStructures.find(s => s.id === selectedUser.customCommissionStructureId);
    return structure || { national: 0, ga: 0, broker: 0 };
  };

  // Security check: Only allow admin users
  if (!isCurrentUserAdmin) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        User Commission Management
      </ModalHeader>
      <ModalBody>
        {!selectedUser ? (
          <div>
            <h5 className="mb-3">Search for User</h5>
            <Row>
              <Col>
                <FormGroup>
                  <Label>Search by name or email</Label>
                  <div className="d-flex gap-2">
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && searchUsers()}
                      placeholder="Enter user name or email..."
                    />
                    <Button
                      color="primary"
                      onClick={searchUsers}
                      disabled={searchLoading || !searchQuery.trim()}
                    >
                      {searchLoading ? <Spinner size="sm" /> : "Search"}
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {searchResults.length > 0 && (
              <div className="mt-3">
                <h6>Search Results:</h6>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Current Commission</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((user) => (
                      <tr key={user.id}>
                        <td>{user.firstName} {user.lastName}</td>
                        <td>{user.email}</td>
                        <td>{getRoleIcon(user.role)}</td>
                        <td>
                          {user.customCommissionTotalAmount
                            ? `$${formatCentsToDollars(user.customCommissionTotalAmount)}`
                            : "$10.00 (default)"
                          }
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleUserSelect(user)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Managing: {selectedUser.firstName} {selectedUser.lastName}
              </h5>
              <Button
                color="secondary"
                size="sm"
                onClick={() => setSelectedUser(null)}
              >
                <i className="ri-arrow-left-line me-1"></i>
                Back to Search
              </Button>
            </div>

            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">User Information</h6>
                  </CardHeader>
                  <CardBody>
                    <p><strong>Name:</strong> {selectedUser.firstName} {selectedUser.lastName}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Role:</strong> {getRoleIcon(selectedUser.role)}</p>
                    <p>
                      <strong>Current Total Amount:</strong>
                      <span className="ms-2">
                        ${formatCentsToDollars(selectedUser.customCommissionTotalAmount || 1000)}
                      </span>
                    </p>
                    <p>
                      <strong>Current Structure:</strong>
                      <span className="ms-2">{getCurrentStructureName()}</span>
                    </p>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Current Commission Breakdown</h6>
                  </CardHeader>
                  <CardBody>
                    {(() => {
                      const structure = getCurrentStructureDetails();
                      return (
                        <div>
                          <p>
                            <strong>National:</strong>
                            <span className="ms-2">${formatCentsToDollars(structure.national)}</span>
                          </p>
                          <p>
                            <strong>GA:</strong>
                            <span className="ms-2">${formatCentsToDollars(structure.ga)}</span>
                          </p>
                          <p>
                            <strong>Broker:</strong>
                            <span className="ms-2">${formatCentsToDollars(structure.broker)}</span>
                          </p>
                          <hr />
                          <p>
                            <strong>Total:</strong>
                            <span className="ms-2">
                              ${formatCentsToDollars(structure.national + structure.ga + structure.broker)}
                            </span>
                          </p>
                        </div>
                      );
                    })()}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <h6 className="mb-0">Update Commission Amount</h6>
                  </CardHeader>
                  <CardBody>
                    <Form>
                      <FormGroup>
                        <Label>Custom Commission Total Amount (cents)</Label>
                        <Input
                          type="number"
                          value={customAmount}
                          onChange={(e) => {
                            setCustomAmount(e.target.value);
                            if (amountError) setAmountError("");
                          }}
                          invalid={!!amountError}
                          placeholder="1000"
                          min="0"
                        />
                        {amountError && <div className="invalid-feedback">{amountError}</div>}
                        <small className="text-muted">
                          Current: ${formatCentsToDollars(selectedUser.customCommissionTotalAmount || 1000)}
                          <br />
                          Default is 1000 cents ($10.00)
                        </small>
                      </FormGroup>
                      <Button
                        color="primary"
                        onClick={handleUpdateAmount}
                        disabled={loading}
                        block
                      >
                        {loading ? <Spinner size="sm" className="me-1" /> : null}
                        Update Amount
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <CardHeader>
                    <h6 className="mb-0">Update Commission Structure</h6>
                  </CardHeader>
                  <CardBody>
                    <Form>
                      <FormGroup>
                        <Label>Commission Structure</Label>
                        <Input
                          type="select"
                          value={selectedStructureId}
                          onChange={(e) => {
                            setSelectedStructureId(e.target.value);
                            if (structureError) setStructureError("");
                          }}
                          invalid={!!structureError}
                        >
                          <option value="">Select structure...</option>
                          <option value="default">Default Structure</option>
                          {commissionStructures.map((structure) => (
                            <option key={structure.id} value={structure.id}>
                              {structure.name}
                            </option>
                          ))}
                        </Input>
                        {structureError && <div className="invalid-feedback">{structureError}</div>}
                        <small className="text-muted">
                          Current: {getCurrentStructureName()}
                        </small>
                      </FormGroup>
                      <Button
                        color="primary"
                        onClick={handleUpdateStructure}
                        disabled={loading}
                        block
                      >
                        {loading ? <Spinner size="sm" className="me-1" /> : null}
                        Update Structure
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UserCommissionManagementModal;